/* ==========================================================================
   WLS Cart Drawer

   Owns the slide-out cart end to end: open/close, quantity changes, line
   removal and upsell adds. Self-contained — it talks to Shopify's Cart AJAX
   API directly and asks it to re-render this section in the same round trip
   (the `sections` parameter). No vendor cart code is involved.

   Opened by:
     - any element carrying [data-wls-cart-open]
     - the `cart:add` document event (fired after add-to-cart)
     - window.WLSCartDrawer.open()

   Kept in sync by the `cart:updated` / `cart:refresh` document events.
   ========================================================================== */

{
  const SECTION_ID = 'wls-cart-drawer';

  const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  const rootUrl = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  class WLSCartDrawer extends HTMLElement {
    connectedCallback() {
      if (this.wired) return;
      this.wired = true;

      this.sectionId = this.dataset.sectionId || SECTION_ID;
      this.isOpen = false;
      this.lastFocused = null;
      this.qtyTimers = {};
      this.queuedRefresh = null;

      this.onKeydown = this.handleKeydown.bind(this);

      this.addEventListener('click', this.handleClick.bind(this));
      this.addEventListener('change', this.handleChange.bind(this));
      document.addEventListener('keydown', this.onKeydown);

      if (document.readyState !== 'complete') {
        window.addEventListener('load', () => this.refreshUpsellNav(), { once: true });
      }

      window.WLSCartDrawer = this;
      this.loadUpsell();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      this.upsellObserver?.disconnect();
    }

    /* --- lookups (the content subtree is replaced, so never cache these) -- */

    get content() {
      return this.querySelector('[data-wls-cart-content]');
    }

    get panel() {
      return this.querySelector('.wls-cart-drawer__panel');
    }

    /* --- open / close --------------------------------------------------- */

    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.lastFocused = document.activeElement;

      const scrollbar = window.innerWidth - document.documentElement.clientWidth;
      if (scrollbar > 0) document.body.style.paddingRight = `${scrollbar}px`;
      document.body.style.overflow = 'hidden';
      document.body.classList.add('wls-cart-drawer-is-open');

      this.setAttribute('aria-hidden', 'false');

      requestAnimationFrame(() => {
        const target = this.panel?.querySelector('[data-wls-cart-close]') || this.panel;
        target?.focus({ preventScroll: true });
      });

      this.refreshUpsellNav();

      document.dispatchEvent(new CustomEvent('wls-cart-drawer:open', { bubbles: true }));
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;

      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      document.body.classList.remove('wls-cart-drawer-is-open');

      this.setAttribute('aria-hidden', 'true');

      if (typeof this.lastFocused?.focus === 'function') {
        this.lastFocused.focus({ preventScroll: true });
      }
      this.lastFocused = null;

      document.dispatchEvent(new CustomEvent('wls-cart-drawer:close', { bubbles: true }));
    }

    toggle() {
      if (this.isOpen) this.close();
      else this.open();
    }

    /* --- events --------------------------------------------------------- */

    handleClick(event) {
      if (event.target.closest('[data-wls-cart-close]')) {
        event.preventDefault();
        this.close();
        return;
      }

      const step = event.target.closest('[data-wls-qty-step]');
      if (step) {
        event.preventDefault();
        this.stepQuantity(step);
        return;
      }

      const remove = event.target.closest('[data-wls-cart-remove]');
      if (remove) {
        event.preventDefault();
        this.changeLine(remove.dataset.wlsKey, 0);
        return;
      }

      const add = event.target.closest('[data-wls-cart-add]');
      if (add) {
        event.preventDefault();
        this.addVariant(add);
        return;
      }

      if (event.target.closest('[data-wls-upsell-prev]')) {
        event.preventDefault();
        this.scrollUpsell(-1);
        return;
      }

      if (event.target.closest('[data-wls-upsell-next]')) {
        event.preventDefault();
        this.scrollUpsell(1);
      }
    }

    handleChange(event) {
      const input = event.target.closest('[data-wls-qty-input]');
      if (input) this.queueQuantity(input.dataset.wlsKey, input.value);
    }

    handleKeydown(event) {
      if (!this.isOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab' || !this.panel) return;

      const focusable = Array.from(this.panel.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement
      );
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /* --- quantity ------------------------------------------------------- */

    stepQuantity(button) {
      const input = button
        .closest('.wls-cart-drawer__qty')
        ?.querySelector('[data-wls-qty-input]');
      if (!input) return;

      const delta = parseInt(button.dataset.wlsQtyStep, 10) || 0;
      let next = (parseInt(input.value, 10) || 0) + delta;
      if (next < 0) next = 0;

      const max = parseInt(input.getAttribute('max'), 10);
      if (!Number.isNaN(max) && next > max) next = max;

      input.value = next;
      this.changeLine(button.dataset.wlsKey, next);
    }

    queueQuantity(key, rawValue) {
      let quantity = parseInt(rawValue, 10);
      if (Number.isNaN(quantity) || quantity < 0) quantity = 0;

      clearTimeout(this.qtyTimers[key]);
      this.qtyTimers[key] = setTimeout(() => this.changeLine(key, quantity), 350);
    }

    /* --- cart mutations ------------------------------------------------- */

    setBusy(busy) {
      this.content?.classList.toggle('is-busy', busy);
    }

    sectionsPayload() {
      return { sections: this.sectionId, sections_url: window.location.pathname };
    }

    static async readJson(response) {
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.description || data.message || `Request failed (${response.status})`);
      }
      return data;
    }

    async changeLine(key, quantity) {
      if (!key) return;
      this.setBusy(true);

      try {
        const response = await fetch(`${rootUrl()}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity, ...this.sectionsPayload() }),
        });
        const cart = await WLSCartDrawer.readJson(response);

        this.applyCart(cart);
        this.broadcast(cart);
      } catch (error) {
        console.error('[wls-cart-drawer] change failed:', error);
        // Re-render from the server so the UI can never drift from the real cart.
        await this.refresh();
      } finally {
        this.setBusy(false);
      }
    }

    async addVariant(button) {
      const id = button.dataset.wlsVariantId;
      if (!id) return;

      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(`${rootUrl()}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: Number(id), quantity: 1 }],
            ...this.sectionsPayload(),
          }),
        });
        const data = await WLSCartDrawer.readJson(response);

        // cart/add.js returns the line item, not the cart, so the count comes
        // from a follow-up read rather than the response body.
        if (data.sections?.[this.sectionId]) {
          this.replaceContent(data.sections[this.sectionId]);
          await this.syncCount();
        } else {
          await this.refresh();
        }

        this.broadcast(null);
      } catch (error) {
        console.error('[wls-cart-drawer] add failed:', error);
      } finally {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
      }
    }

    /* --- rendering ------------------------------------------------------ */

    applyCart(cart) {
      if (cart?.sections?.[this.sectionId]) {
        this.replaceContent(cart.sections[this.sectionId]);
        if (typeof cart.item_count === 'number') {
          this.dataset.itemCount = cart.item_count;
          this.syncCount(cart.item_count);
        }
        return;
      }
      this.refresh();
    }

    replaceContent(markup) {
      if (!markup) return;

      const parsed = new DOMParser().parseFromString(markup, 'text/html');
      const fresh = parsed.querySelector('[data-wls-cart-content]');
      const current = this.content;

      // Bailing silently here is how a stale drawer hides a broken render.
      if (!fresh || !current) {
        console.error(
          '[wls-cart-drawer] could not swap content:',
          !fresh ? 'no [data-wls-cart-content] in the response' : 'no [data-wls-cart-content] in the DOM'
        );
        return;
      }

      current.innerHTML = fresh.innerHTML;

      const root = parsed.querySelector('[data-wls-cart-drawer]');
      if (root?.dataset.itemCount) this.dataset.itemCount = root.dataset.itemCount;

      this.loadUpsell();
    }

    /* Re-reads the cart and re-renders.

       Goes through the Cart AJAX API rather than a GET on `?sections=`: the
       GET is a plain cacheable request, so right after an add-to-cart it can
       come back with the pre-add cart and the drawer opens without the new
       line. POST /cart/update.js with an empty `updates` mutates nothing,
       is never cached, and returns the live cart plus freshly rendered
       sections in one round trip — the same mechanism the in-drawer quantity
       and add actions already use. */
    async fetchAndRender() {
      try {
        const response = await fetch(`${rootUrl()}cart/update.js`, {
          method: 'POST',
          cache: 'no-store',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: {}, ...this.sectionsPayload() }),
        });
        const cart = await WLSCartDrawer.readJson(response);

        const markup = cart.sections?.[this.sectionId];
        if (!markup) throw new Error(`Section "${this.sectionId}" missing from the response`);

        this.replaceContent(markup);
        this.dataset.itemCount = cart.item_count;
        await this.syncCount(cart.item_count);
      } catch (error) {
        console.error('[wls-cart-drawer] refresh failed:', error);
      }
    }

    /* Add-to-cart fires cart:add, cart:updated and cart:refresh back to back,
       so the burst is collapsed into one request. Unlike handing back an
       in-flight promise, this always issues the request AFTER the last event,
       so the response can never predate the mutation that triggered it. */
    refresh() {
      if (this.queuedRefresh) return this.queuedRefresh;

      this.queuedRefresh = new Promise((resolve) => {
        setTimeout(async () => {
          this.queuedRefresh = null;
          await this.fetchAndRender();
          resolve();
        }, 50);
      });

      return this.queuedRefresh;
    }

    /* --- cart count badge ----------------------------------------------- */

    async syncCount(knownCount) {
      const nodes = document.querySelectorAll('[data-cart-count]');
      if (!nodes.length) return;

      if (typeof knownCount === 'number') {
        nodes.forEach((node) => {
          node.textContent = knownCount;
        });
        return;
      }

      try {
        const response = await fetch(`${rootUrl()}cart.js`, { headers: { Accept: 'application/json' } });
        const cart = await response.json();
        this.dataset.itemCount = cart.item_count;
        nodes.forEach((node) => {
          node.textContent = cart.item_count;
        });
      } catch {
        /* the badge is cosmetic — never block the drawer on it */
      }
    }

    broadcast(cart) {
      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          bubbles: true,
          detail: { source: SECTION_ID, cart },
        })
      );
    }

    /* --- upsell rail ---------------------------------------------------- */

    /* "You may also need" cards are rendered server-side by the section from
       the cart lines' `custom.wls_cart_upsell` metafield — here we only wire
       up the arrows. */
    loadUpsell() {
      const track = this.querySelector('[data-wls-upsell-track]');
      if (!track) return;

      this.bindUpsellScroll(track);
      this.refreshUpsellNav();
      requestAnimationFrame(() => this.refreshUpsellNav());
    }

    /* The track element is replaced on every render, so the listener is bound
       against the element itself rather than a flag on the host.

       The size observer matters as much as the scroll one: the drawer's
       stylesheet loads async, so the first measurement can happen while the
       track is still an unstyled block with no overflow. That would disable
       both arrows permanently — nothing would ever re-measure, because a
       disabled arrow can neither be clicked nor scrolled. Re-measuring on
       every size change also covers font loading, resize and rotation. */
    bindUpsellScroll(track) {
      if (!this.upsellObserver && typeof ResizeObserver !== 'undefined') {
        this.upsellObserver = new ResizeObserver(() => this.refreshUpsellNav());
      }

      if (this.upsellObserver) {
        this.upsellObserver.disconnect();
        this.upsellObserver.observe(track);
      }

      if (track.dataset.wlsScrollBound) return;
      track.dataset.wlsScrollBound = 'true';
      track.addEventListener('scroll', () => this.refreshUpsellNav(), { passive: true });
    }

    scrollUpsell(direction) {
      const track = this.querySelector('[data-wls-upsell-track]');
      if (!track) return;

      const card = track.querySelector('.wls-cart-drawer__upsell-card');
      const step = card ? card.getBoundingClientRect().width + 10 : track.clientWidth;

      track.scrollBy({ left: step * direction, behavior: 'smooth' });
      setTimeout(() => this.refreshUpsellNav(), 400);
    }

    refreshUpsellNav() {
      const track = this.querySelector('[data-wls-upsell-track]');
      const prev = this.querySelector('[data-wls-upsell-prev]');
      const next = this.querySelector('[data-wls-upsell-next]');
      if (!track || !prev || !next) return;

      const maxScroll = track.scrollWidth - track.clientWidth;
      prev.disabled = track.scrollLeft <= 1;
      next.disabled = track.scrollLeft >= maxScroll - 1;
    }
  }

  if (!customElements.get('wls-cart-drawer')) {
    customElements.define('wls-cart-drawer', WLSCartDrawer);
  }

  /* --- global wiring ---------------------------------------------------- */

  const drawer = () => document.querySelector('[data-wls-cart-drawer]');

  // Delegated so triggers injected after load still work.
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-wls-cart-open]');
    if (!trigger) return;

    const el = drawer();
    if (!el) return; // no drawer on this template (e.g. /cart) — let the link navigate

    event.preventDefault();
    el.open();
  });

  // Add-to-cart anywhere on the page pulls the drawer open.
  document.addEventListener('cart:add', async () => {
    const el = drawer();
    if (!el) return;
    await el.refresh();
    el.open();
  });

  // Stay in sync when another component mutates the cart.
  ['cart:updated', 'cart:refresh'].forEach((name) => {
    document.addEventListener(name, (event) => {
      if (event.detail?.source === SECTION_ID) return;
      drawer()?.refresh();
    });
  });

  /* --- fetch interceptor --------------------------------------------------
     Legacy templates (T4S) do their own AJAX add-to-cart but never fire the
     `cart:add` event that the drawer listens for.  Wrapping `fetch` lets us
     detect *any* successful POST to /cart/add.js — no matter who made the
     call — and broadcast the event so the drawer opens and refreshes.

     Safe when `wls-main-product` is also on the page: its own `cart:add`
     dispatch simply doubles up, but `refresh()` is debounced (50 ms) and
     `open()` is idempotent, so nothing breaks.
     ---------------------------------------------------------------------- */
  const nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : input?.url || '';
    const method = (init?.method || 'GET').toUpperCase();

    const result = nativeFetch.apply(this, arguments);

    if (method === 'POST' && /\/cart\/add(?:\.js|\.json)?(?:[?#]|$)/.test(url)) {
      result.then((response) => {
        if (response.ok) {
          document.dispatchEvent(
            new CustomEvent('cart:add', {
              detail: { source: 'fetch-interceptor' },
              bubbles: true,
            })
          );
        }
      }).catch(() => {});
    }

    return result;
  };
}
